import type { ReactNode } from "react";
import type { UseFernieChatReturn } from "~/hooks/useFernieChat";

import {
	alpha,
	Box,
	ButtonBase,
	Icon,
	IconButton,
	type SxProps,
	TextField,
	type Theme,
	Tooltip,
	Typography
} from "@mui/material";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";

import FernieActivityGroup from "~/components/FernieActivityGroup";
import FernieArtifactCard from "~/components/FernieArtifactCard";
import FernieConfirmCard from "~/components/FernieConfirmCard";
import FernieExpiredReportCard from "~/components/FernieExpiredReportCard";
import FernieIntakeCard from "~/components/FernieIntakeCard";
import FernieMarkdown from "~/components/FernieMarkdown";
import FernieNavConfirmCard from "~/components/FernieNavConfirmCard";
import FernieStreamProgress from "~/components/FernieStreamProgress";
import FernieAnswerLayoutCard from "~/components/fernie/FernieAnswerLayoutCard";
import FernieAnswerRating from "~/components/fernie/FernieAnswerRating";
import FernieDictationBar from "~/components/fernie/FernieDictationBar";
import FernieFace from "~/components/fernie/FernieFace";
import FernieFaceAnimated from "~/components/fernie/FernieFaceAnimated";
import FernieMigrationReview from "~/components/fernie/FernieMigrationReview";
import FernieSenderSetup from "~/components/fernie/FernieSenderSetup";
import FernieSetupFiles from "~/components/fernie/FernieSetupFiles";
import fernieButtonSx from "~/components/fernie/fernieButtonSx";
import { BodyTextSmall, CaptionText, SubtitleText } from "~/components/Typographies";
import answerLayouts from "~/helpers/aiChat/answerLayouts";
import answerRating from "~/helpers/aiChat/answerRating";
import openInNewTab from "~/helpers/aiChat/openInNewTab";
import reportDraftFiles from "~/helpers/aiChat/reportDraftFiles";
import { formatFileSize } from "~/helpers/csvValidators";
import useFernieDictation from "~/hooks/useFernieDictation";

const FernieReportDraft = lazy(() => import("~/components/fernie/FernieReportDraft"));

interface FernieConversationProps {
	chat: UseFernieChatReturn;
	emptyState?: ReactNode;
}

const SUGGESTION_SX = {
	"&:hover, &.Mui-focusVisible": {
		backgroundColor: (theme: Theme) => alpha(theme.palette.fernie.fern, 0.12),
		color: "text.primary"
	},
	backgroundColor: (theme: Theme) => alpha(theme.palette.fernie.fern, theme.palette.mode === "dark" ? 0.1 : 0.045),
	borderRadius: "10px",
	color: "text.primary",
	cursor: "pointer",
	fontSize: "0.8rem",
	gap: 1.5,
	justifyContent: "space-between",
	lineHeight: 1.5,
	minHeight: 40,
	px: 1.5,
	py: 1,
	textAlign: "left",
	transition: "all 0.15s"
} satisfies SxProps<Theme>;

const FernieConversation = ({ chat, emptyState }: FernieConversationProps) => {
	const {
		bottomRef,
		busyCaptureId,
		busyProposalId,
		faceState,
		fileInputRef,
		handleCancelIntake,
		handleCancelProposal,
		handleCancelStream,
		handleChooseNav,
		handleConfirmProposal,
		handleFile,
		handleRateMessage,
		handleRegenerateReport,
		handleSubmitIntake,
		input,
		inputRef,
		lastAssistantIndex,
		loading,
		messages,
		migrationReview,
		pendingFile,
		ratingPendingIds,
		reportDraft,
		routeWalkthroughs,
		send,
		setInput,
		setInputFocused,
		setPendingFile,
		setRunOutcome,
		setupFiles,
		suggestions
	} = chat;
	const { t } = useTranslation("common");
	const dictation = useFernieDictation({ inputRef, setDraft: setInput });
	const isDictating = dictation.phase !== "idle";
	const dictationLabel = t(isDictating ? "fernieChat.dictation.stop" : "fernieChat.dictation.start");
	const draftSuggestion = reportDraft.isOffered && (
		<Box>
			<ButtonBase onClick={reportDraft.start} sx={{ ...SUGGESTION_SX, width: "100%" }}>
				{t("fernieChat.reportDraft.suggestion")}
				<Icon sx={{ color: "text.disabled", flexShrink: 0, fontSize: 16 }}>arrow_forward</Icon>
			</ButtonBase>
			<CaptionText sx={{ color: "text.secondary", display: "block", fontSize: "0.625rem", mt: 0.75 }}>
				{t("fernieChat.reportDraft.suggestionCaption")}
			</CaptionText>
		</Box>
	);

	return (
		<>
			<input
				accept={reportDraftFiles.chatFileAccept({
					acceptsSetupFiles: setupFiles.acceptsSetupFiles,
					isDraftOffered: reportDraft.isOffered,
					isDraftStarted: reportDraft.isStarted
				})}
				aria-label={t("fernieChat.attachFile")}
				hidden
				multiple={setupFiles.acceptsSetupFiles && !reportDraft.isStarted}
				onChange={handleFile}
				ref={fileInputRef}
				type="file"
			/>

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					minWidth: 0,
					overflowX: "hidden",
					overflowY: "auto",
					overscrollBehavior: "contain",
					px: 2.5,
					py: 2
				}}
			>
				{messages.length === 0 && emptyState}
				{messages.length === 0 && !emptyState && !reportDraft.file && (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							maxWidth: 360,
							minHeight: reportDraft.isStarted ? undefined : "100%",
							mx: "auto",
							pb: 3,
							pt: 2,
							textAlign: "center"
						}}
					>
						<FernieFace decorative size={52} state="rest" sx={{ alignSelf: "center", mb: 3 }} />
						<SubtitleText bold sx={{ color: "text.primary", fontSize: "1.3rem", letterSpacing: "-0.035em", mb: 1 }}>
							{t("fernieChat.welcome.title")}
						</SubtitleText>
						<BodyTextSmall
							sx={{
								color: "text.secondary",
								fontSize: "0.82rem",
								lineHeight: 1.5,
								mb: 3,
								px: 1.5,
								wordWrap: "break-word"
							}}
						>
							{t("fernieChat.welcome.subtitle")}
						</BodyTextSmall>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mx: "auto", width: "100%" }}>
							{reportDraft.leadsSuggestions && draftSuggestion}
							{routeWalkthroughs.map((walkthrough) => (
								<ButtonBase
									key={walkthrough.id}
									onClick={() => send(t("fernieChat.welcome.tourPrompt", { title: t(walkthrough.titleKey) }))}
									sx={{
										"&:hover": { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12) },
										alignItems: "center",
										backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
										border: "1px solid",
										borderColor: "primary.main",
										borderRadius: "10px",
										color: "primary.main",
										cursor: "pointer",
										display: "flex",
										fontSize: "0.8rem",
										fontWeight: 600,
										gap: 0.75,
										px: 1.5,
										py: 1,
										textAlign: "left",
										transition: "all 0.15s"
									}}
								>
									<Icon sx={{ fontSize: 16 }}>tour</Icon>
									{t("fernieChat.welcome.tourScreen", { title: t(walkthrough.titleKey) })}
								</ButtonBase>
							))}
							{suggestions.map((s) => (
								<ButtonBase key={s} onClick={() => send(s)} sx={SUGGESTION_SX}>
									{s}
									<Icon sx={{ color: "text.disabled", flexShrink: 0, fontSize: 16 }}>arrow_forward</Icon>
								</ButtonBase>
							))}
							{!reportDraft.leadsSuggestions && draftSuggestion}
						</Box>
					</Box>
				)}

				{messages.map((m, i) => (
					<Box key={i} sx={{ display: "flex", flexDirection: "column", mb: 3, minWidth: 0, overflowWrap: "anywhere" }}>
						{m.role === "user" ? (
							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Box
									sx={{
										backgroundColor: (theme) => alpha(theme.palette.fernie.fern, 0.12),
										borderRadius: "16px 16px 4px 16px",
										color: "text.primary",
										maxWidth: "85%",
										px: 2,
										py: 1.25
									}}
								>
									{m.fileName && (
										<Box
											sx={{
												alignItems: "center",
												backgroundColor: (theme) => alpha(theme.palette.common.white, 0.15),
												borderRadius: "6px",
												display: "inline-flex",
												fontSize: "0.72rem",
												gap: 0.5,
												mb: 0.75,
												px: 1,
												py: 0.25
											}}
										>
											<Icon sx={{ fontSize: 14 }}>description</Icon>
											{m.fileName}
										</Box>
									)}
									<Typography sx={{ fontSize: "0.875rem", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
										{m.content}
									</Typography>
								</Box>
							</Box>
						) : (
							<Box sx={{ display: "flex", gap: 1 }}>
								<Box sx={{ flexShrink: 0, mt: 0.5 }}>
									<FernieFaceAnimated decorative size={24} state={i === lastAssistantIndex ? faceState : "rest"} />
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<FernieMarkdown content={m.content} />

									{m.streaming && (
										<FernieStreamProgress
											hasContent={m.content.trim().length > 0}
											onStop={handleCancelStream}
											steps={m.steps ?? []}
											waitingLabel={t("fernieChat.panel.thinking")}
										/>
									)}

									{m.cancelled && (
										<CaptionText sx={{ color: "text.disabled", fontStyle: "italic", mt: 0.5 }}>
											{t("fernieChat.panel.responseCancelled")}
										</CaptionText>
									)}

									{m.htmlArtifacts?.map((html, j) => (
										<Box
											key={j}
											sx={{
												border: "1px solid",
												borderColor: "divider",
												borderRadius: "12px",
												mt: 1.5,
												overflow: "hidden"
											}}
										>
											<Box
												sx={{
													backgroundColor: "white",
													height: 180,
													overflow: "hidden",
													position: "relative"
												}}
											>
												<Box
													component="iframe"
													sandbox="allow-scripts"
													srcDoc={html}
													sx={{
														border: "none",
														height: 240,
														pointerEvents: "none",
														transform: "scale(0.75)",
														transformOrigin: "top left",
														width: "133%"
													}}
													title={t("fernieChat.panel.reportTitle", { number: j + 1 })}
												/>
												<Box
													sx={{
														background: "linear-gradient(transparent 40%, white)",
														bottom: 0,
														left: 0,
														position: "absolute",
														right: 0,
														top: 0
													}}
												/>
											</Box>
											<Box
												sx={{
													alignItems: "center",
													backgroundColor: "action.hover",
													display: "flex",
													gap: 1,
													px: 1.5,
													py: 0.75
												}}
											>
												<Icon sx={{ color: "primary.main", fontSize: 16 }}>article</Icon>
												<Typography sx={{ color: "text.secondary", flex: 1, fontSize: "0.75rem", fontWeight: 500 }}>
													{t("fernieChat.panel.generatedReport")}
												</Typography>
												<ButtonBase
													onClick={() => openInNewTab(html)}
													sx={{
														...fernieButtonSx.focusRing,
														"&:hover": { backgroundColor: "primary.dark" },
														backgroundColor: "primary.main",
														borderRadius: "6px",
														color: "white",
														cursor: "pointer",
														font: "inherit",
														fontSize: "0.72rem",
														fontWeight: 500,
														px: 1.25,
														py: 0.4,
														transition: "background-color 0.15s"
													}}
												>
													{t("fernieChat.panel.openReport")}
												</ButtonBase>
											</Box>
										</Box>
									))}

									{answerLayouts.drawable(m.layouts).map((layout, j) => (
										<FernieAnswerLayoutCard key={`${layout.title}${j}`} layout={layout} />
									))}

									{answerLayouts.reportArtifactsToShow(m.reportArtifacts, m.layouts)?.map((artifact) => (
										<FernieArtifactCard artifact={artifact} key={artifact.filename} />
									))}

									{answerLayouts.reportArtifactsToShow(m.unavailableReportArtifacts, m.layouts)?.map((artifact) => (
										<FernieExpiredReportCard
											artifact={artifact}
											key={artifact.filename}
											onRegenerate={() => handleRegenerateReport(artifact.filename)}
										/>
									))}

									{m.proposals?.map((p) => (
										<FernieConfirmCard
											busy={busyProposalId === p.id}
											key={p.id}
											onCancel={() => handleCancelProposal(p.id)}
											onConfirm={() => handleConfirmProposal(p.id)}
											proposal={p}
										/>
									))}

									{m.intakeDrafts?.map((draft) => (
										<FernieIntakeCard
											busy={busyCaptureId === draft.captureId}
											draft={draft}
											key={draft.captureId}
											onCancel={() => handleCancelIntake(draft.captureId)}
											onSubmit={() => handleSubmitIntake(draft.captureId)}
										/>
									))}

									{m.navConfirmation && m.id !== undefined && (
										<FernieNavConfirmCard
											chosenPath={m.chosenNavPath}
											confirmation={m.navConfirmation}
											onChoose={(candidate) => handleChooseNav(m.id ?? "", candidate)}
										/>
									)}

									{m.toolResults && <FernieActivityGroup results={m.toolResults} />}

									{answerRating.canRateAnswer(m) && (
										<FernieAnswerRating
											isPending={ratingPendingIds.includes(m.serverId ?? "")}
											message={m}
											onRate={handleRateMessage}
										/>
									)}
								</Box>
							</Box>
						)}
					</Box>
				))}

				<FernieMigrationReview review={migrationReview} />
				<FernieSenderSetup isActive={setupFiles.isJourneyConversation} />
				{reportDraft.isStarted && (
					<Suspense fallback={null}>
						<FernieReportDraft
							draft={reportDraft}
							key={reportDraft.runKey}
							onAttach={() => fileInputRef.current?.click()}
						/>
					</Suspense>
				)}

				<div ref={bottomRef} />
			</Box>

			<FernieSetupFiles setupFiles={setupFiles} />

			{pendingFile && (
				<Box
					sx={{
						alignItems: "center",
						borderTop: "1px solid",
						borderTopColor: "divider",
						display: "flex",
						gap: 1,
						px: 2,
						py: 0.75
					}}
				>
					<Box
						sx={{
							alignItems: "center",
							backgroundColor: "action.hover",
							borderRadius: "6px",
							display: "flex",
							fontSize: "0.75rem",
							gap: 0.5,
							px: 1,
							py: 0.5
						}}
					>
						<Icon sx={{ color: "text.secondary", fontSize: 14 }}>description</Icon>
						<Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>{pendingFile.name}</Typography>
						<Typography sx={{ color: "text.disabled", fontSize: "0.7rem" }}>
							{formatFileSize(pendingFile.size)}
						</Typography>
						<IconButton
							aria-label={t("fernieChat.removeFile")}
							onClick={() => setPendingFile(null)}
							size="small"
							sx={{ height: 16, ml: 0.25, width: 16 }}
						>
							<Icon sx={{ fontSize: 12 }}>close</Icon>
						</IconButton>
					</Box>
				</Box>
			)}

			<Box
				sx={{
					backgroundColor: "background.default",
					boxShadow: (theme) => `0 -12px 24px ${alpha(theme.palette.background.default, 0.92)}`,
					flexShrink: 0,
					pb: 1.5,
					pt: 1,
					px: 2
				}}
			>
				<FernieDictationBar dictation={dictation} />
				<Box
					sx={{
						"&:focus-within": {
							borderColor: "primary.main",
							boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.fernie.fern, 0.08)}`
						},
						alignItems: "flex-end",
						backgroundColor: "background.paper",
						border: "1px solid",
						borderColor: "divider",
						borderRadius: "16px",
						boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.fernie.tile, 0.035)}`,
						display: "flex",
						px: 1,
						py: 0.5,
						transition: "border-color 0.15s"
					}}
				>
					<Tooltip title={t("fernieChat.attachFile")}>
						<span>
							<IconButton
								aria-label={t("fernieChat.attachFile")}
								disabled={loading || setupFiles.isResolvingJourney}
								onClick={() => fileInputRef.current?.click()}
								size="small"
								sx={{ "&:hover": { color: "text.primary" }, color: "text.disabled", mb: 0.25 }}
							>
								<Icon sx={{ fontSize: 18 }}>attach_file</Icon>
							</IconButton>
						</span>
					</Tooltip>
					<TextField
						fullWidth
						inputRef={inputRef}
						maxRows={4}
						multiline
						onBlur={() => setInputFocused(false)}
						onChange={(e) => {
							setRunOutcome(null);
							setInput(e.target.value);
						}}
						onFocus={() => {
							setRunOutcome(null);
							setInputFocused(true);
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter" && !e.shiftKey) {
								e.preventDefault();
								if (!isDictating) send(input);
							}
						}}
						placeholder={
							pendingFile
								? t("fernieChat.panel.filePlaceholder", { name: pendingFile.name })
								: t("fernieChat.panel.placeholder")
						}
						size="small"
						slotProps={{
							input: {
								sx: {
									"& fieldset": { border: "none" },
									"@media (pointer: coarse)": { fontSize: "16px" },
									fontSize: "0.875rem",
									py: 0.75
								}
							}
						}}
						value={input}
						variant="outlined"
					/>
					{dictation.isAvailable && (
						<Tooltip title={dictationLabel}>
							<span>
								<IconButton
									aria-label={dictationLabel}
									disabled={dictation.phase === "transcribing"}
									onClick={isDictating ? dictation.stop : dictation.start}
									size="small"
									sx={{
										"&:hover": { color: "text.primary" },
										color: isDictating ? "primary.main" : "text.disabled",
										mb: 0.25
									}}
								>
									<Icon sx={{ fontSize: 18 }}>{isDictating ? "stop_circle" : "mic"}</Icon>
								</IconButton>
							</span>
						</Tooltip>
					)}
					<IconButton
						aria-label={t("fernieChat.send")}
						disabled={loading || isDictating || (!input.trim() && !pendingFile)}
						onClick={() => send(input)}
						size="small"
						sx={{
							"&:disabled": { backgroundColor: "transparent", color: "text.disabled" },
							"&:hover": { backgroundColor: (theme) => theme.palette.fernie.fern },
							backgroundColor: (theme) => theme.palette.fernie.led,
							borderRadius: "8px",
							color: (theme) => theme.palette.fernie.tile,
							height: 30,
							mb: 0.25,
							transition: "all 0.15s",
							width: 30
						}}
					>
						<Icon sx={{ fontSize: 16 }}>arrow_upward</Icon>
					</IconButton>
				</Box>
				<CaptionText
					sx={{
						color: "text.secondary",
						display: "block",
						fontSize: "0.625rem",
						mt: 1,
						opacity: 0.65,
						textAlign: "center"
					}}
				>
					{t("fernieChat.panel.disclaimer")}
				</CaptionText>
			</Box>
		</>
	);
};

export default FernieConversation;
